import { previewHall } from "../../hall-preview";
import type { Criteria } from "../../search";
import type { Hall } from "../../types";
import { SeatPlan } from "../SeatPlan";

type Props = {
  halls: Hall[];
  index: number;
  onIndex: (index: number) => void;
  criteria: Criteria;
  showGroup: boolean;
  note?: string;
};

/**
 * La salle reste affichée pendant tout l'assistant : on ne change que ce qui est
 * mis en avant dessus. C'est le même repère visuel d'une étape à l'autre.
 */
export function HallStage({ halls, index, onIndex, criteria, showGroup, note }: Props) {
  if (halls.length === 0) {
    return (
      <div className="stage is-empty">
        <p className="state">
          Aucune salle en mémoire pour ce cinéma. Les plans se remplissent au fil des
          consultations.
        </p>
      </div>
    );
  }

  const hall = halls[Math.min(index, halls.length - 1)];
  const { view, placed } = previewHall(hall.plan, {
    depth: criteria.depth,
    side: criteria.side,
    people: showGroup ? criteria.people : 0,
    layout: criteria.layout,
  });

  const move = (step: number) => onIndex((index + step + halls.length) % halls.length);

  // le plan remplit le cadre quelle que soit la salle : la fenêtre ne bouge jamais
  const seatSize = Math.max(
    4,
    Math.min(13, Math.floor(Math.min(540 / hall.plan.columns, 168 / view.rows.length)) - 2),
  );

  return (
    <div className="stage">
      <div className="stage-plan">
        {halls.length > 1 && (
          <button
            type="button"
            className="stage-arrow"
            onClick={() => move(-1)}
            aria-label="Salle précédente"
          >
            ‹
          </button>
        )}

        <div className="stage-view" style={{ "--seat": `${seatSize}px` } as React.CSSProperties}>
          <SeatPlan plan={view} variant="stage" screen />
        </div>

        {halls.length > 1 && (
          <button
            type="button"
            className="stage-arrow"
            onClick={() => move(1)}
            aria-label="Salle suivante"
          >
            ›
          </button>
        )}
      </div>

      <ul className="legend is-stage">
        <li>
          <span className="cell is-picked" /> vos places
        </li>
        <li>
          <span className="cell is-free" /> zone retenue
        </li>
        <li>
          <span className="cell is-sold" /> hors zone
        </li>
        <li>
          <span className="cell is-pmr" /> PMR
        </li>
      </ul>

      <p className="stage-caption">
        <strong>Salle {hall.auditorium}</strong>
        <span>
          {hall.capacity} places · {hall.plan.rows.length} rangées
          {halls.length > 1 && ` · ${index + 1}/${halls.length}`}
        </span>
        {showGroup && placed > 0 && (
          <span className="stage-hint">exemple de placement pour {placed} personnes</span>
        )}
        {showGroup && placed === 0 && criteria.people > 0 && (
          <span className="stage-hint is-warn">groupe non plaçable dans cette zone</span>
        )}
        {note && <span className="stage-hint">{note}</span>}
      </p>
    </div>
  );
}
